import { Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, ExternalLink } from "lucide-react";
import { PriceDisplay } from "@/components/PriceDisplay";
import { SaleRibbon } from "@/components/SaleRibbon";

interface Props {
  hubSlug: string;
  hubTitle: string;
  limit?: number;
}

// Hubs with their own dedicated table. Anything else uses `products` filtered by product_type.
const DEDICATED: Record<string, string> = {
  "lawn-mowers": "lawn_mowers",
  generators: "generators",
  chainsaws: "chainsaws",
  "pressure-washers": "pressure_washers",
  "impact-drivers": "impact_drivers",
};

interface Row {
  id: string;
  brand: string;
  model: string;
  slug: string;
  image_url: string | null;
  description: string | null;
  rating: number | null;
  best_for: string | null;
  price_gbp: number | null;
  previous_price_gbp: number | null;
  availability: string | null;
  price_last_checked: string | null;
  price_drop_at: string | null;
  sale_end_date: string | null;
  affiliate_link: string | null;
}

const HubTopProducts = ({ hubSlug, hubTitle, limit = 6 }: Props) => {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["hub-top-products", hubSlug, limit],
    queryFn: async (): Promise<Row[]> => {
      const dedicated = DEDICATED[hubSlug];
      if (dedicated) {
        const { data, error } = await supabase
          .from(dedicated as any)
          .select(
            "id, brand, model, slug, image_url, description, rating, best_for, price_gbp, previous_price_gbp, availability, price_last_checked, price_drop_at, sale_end_date, affiliate_link"
          )
          .eq("status", "published")
          .order("rating", { ascending: false, nullsFirst: false })
          .limit(limit);
        if (error) throw error;
        return (data || []) as any;
      }
      const { data, error } = await supabase
        .from("products")
        .select(
          "id, brand, model, slug, image_url, description, rating, best_for, price_gbp, previous_price_gbp, availability, price_last_checked, price_drop_at, sale_end_date, affiliate_link"
        )
        .eq("product_type", hubSlug)
        .eq("status", "published")
        .order("rating", { ascending: false, nullsFirst: false })
        .limit(limit);
      if (error) throw error;
      return (data || []) as any;
    },
  });

  if (isLoading || items.length === 0) return null;

  return (
    <section aria-label={`Top ${hubTitle} reviewed`} className="border-b bg-card">
      <div className="container mx-auto px-4 py-10 max-w-6xl">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Top {hubTitle} Reviewed
            </h2>
            <p className="text-sm text-muted-foreground">
              Independent UK ratings — sorted by review score.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to={`/${hubSlug}/reviews`}>See all reviews</Link>
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <Card
              key={p.id}
              className="overflow-hidden bg-card hover:shadow-lg transition-shadow group"
            >
              {p.image_url && (
                <Link
                  to={`/${hubSlug}/${p.slug}`}
                  className="block aspect-[4/3] overflow-hidden bg-white relative"
                >
                  <SaleRibbon
                    price={p.price_gbp}
                    previousPrice={p.previous_price_gbp}
                    priceDropAt={p.price_drop_at}
                    saleEndDate={p.sale_end_date}
                  />
                  <img
                    src={p.image_url}
                    alt={`${p.brand} ${p.model}`}
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </Link>
              )}
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                      {p.brand}
                    </p>
                    <h3 className="font-bold text-foreground leading-tight">
                      <Link
                        to={`/${hubSlug}/${p.slug}`}
                        className="hover:text-primary transition-colors"
                      >
                        {p.brand} {p.model}
                      </Link>
                    </h3>
                  </div>
                  {p.rating && (
                    <Badge variant="secondary" className="shrink-0 gap-1">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                      {p.rating}
                    </Badge>
                  )}
                </div>

                {p.best_for && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    <span className="font-medium text-foreground">Best for:</span> {p.best_for}
                  </p>
                )}

                <div className="flex items-center justify-between pt-3 border-t gap-2 flex-wrap">
                  {p.price_gbp ? (
                    <PriceDisplay
                      price={p.price_gbp}
                      previousPrice={p.previous_price_gbp}
                      availability={p.availability}
                      lastChecked={p.price_last_checked}
                      priceDropAt={p.price_drop_at}
                      saleEndDate={p.sale_end_date}
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground">Price TBC</span>
                  )}
                  <div className="flex gap-2">
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/${hubSlug}/${p.slug}`}>Review</Link>
                    </Button>
                    {p.affiliate_link && (
                      <Button asChild size="sm">
                        <a
                          href={p.affiliate_link}
                          target="_blank"
                          rel="noopener noreferrer sponsored"
                        >
                          Price <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HubTopProducts;
